import React, { useState, useEffect } from "react";
import { useFilteredInsights } from "@/hooks/useFilteredInsights";
import { InsightCard as InsightCardType } from "@/types/insight";
import InsightCard from "@/components/insights/InsightCard";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { useNavigate } from "react-router-dom";

const FeaturedInsightsSection = () => {
  const { insights: fetchedInsights, isLoading } = useFilteredInsights(
    null,
    null,
    5
  );
  const [insights, setInsights] = useState<InsightCardType[]>([]);
  const navigate = useNavigate();

  // Transform fetched insights to match InsightCard type
  useEffect(() => {
    if (fetchedInsights && fetchedInsights.length > 0) {
      const transformedInsights: InsightCardType[] = fetchedInsights.map(
        (insight) => ({
          id: insight.id,
          title: insight.title,
          category: insight.category?.name || "",
          agreePercentage: insight.agree_percentage || 0,
          totalPollsters: insight.total_pollsters || 0,
          description: insight.content || "",
          status: insight.published ? "published" : "draft",
          country_code: insight.country_code,
          constituency_code: null,
          linked_poll: insight.linked_poll,
          stats_status: insight.stats_status,
          slug: insight.slug || null,
        })
      );
      setInsights(transformedInsights);
    }
  }, [fetchedInsights]);

  const handleViewInsight = (card: InsightCardType) => {
    if (card.slug) {
      navigate(`/insight/${card.slug}`);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold mb-4 text-nation-blue dark:text-blue-300">
          Featured Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg overflow-hidden shadow-md">
              <Skeleton className="h-40 w-full" />
              <div className="p-4 space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!insights.length) {
    return (
      <div className="text-center p-10 bg-muted/20 rounded-lg">
        <h3 className="text-xl font-medium mb-2">
          No featured insights available
        </h3>
        <p>Check back later for new insights</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-nation-blue dark:text-blue-300">
        Featured Insights
      </h2>

      <Carousel className="w-full">
        <CarouselContent className="-ml-4">
          {insights.map((insight) => (
            <CarouselItem
              key={insight.id}
              className="pl-4 md:basis-1/2 lg:basis-1/3"
            >
              <div className="h-full">
                <InsightCard
                  card={insight}
                  onViewInsights={() => handleViewInsight(insight)}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-end gap-2 mt-4">
          <CarouselPrevious className="static relative transform-none" />
          <CarouselNext className="static relative transform-none" />
        </div>
      </Carousel>
    </div>
  );
};

export default FeaturedInsightsSection;
